import { createGlobeController, type GlobeController, type GlobeSnapshot } from '@colescomex/globe-runtime';
import { JOURNEY_FRAMES, ROUTE_REVEAL, globeConfiguration, type DeviceProfile } from './narrative';
import type { Locale } from '../i18n/locale';
import { prefersReducedMotion } from '../motion/preferences';

/**
 * Owns the single globe engine of the HOME. Two states: the free hero globe and the scroll-scrubbed
 * journey. The engine is paused whenever no section shows it. Owns no render loop.
 */
export class GlobeDirector {
  private controller: GlobeController | null = null;
  private journey = -1;
  private active = false;
  private listeners = new Set<(snapshot: GlobeSnapshot) => void>();
  snapshot: GlobeSnapshot = { phase: 'UNMOUNTED', reason: null };

  subscribe(listener: (snapshot: GlobeSnapshot) => void) { this.listeners.add(listener); listener(this.snapshot); return () => { this.listeners.delete(listener); }; }

  async mount(container: HTMLElement, profile: DeviceProfile, locale: Locale = 'es-CO') {
    const controller = createGlobeController({ onState: snapshot => { this.snapshot = snapshot; for (const listener of this.listeners) listener(snapshot); } });
    this.controller = controller;
    await controller.mount(container, globeConfiguration(profile, locale));
    if (this.controller !== controller) return;
    // Kit traffic (three.js + GLTF loader) streams in after the planet is on screen.
    void import('./traffic').then(({ createTrafficLayer }) => { if (this.controller === controller) controller.attachLayer(createTrafficLayer({ reducedMotion: prefersReducedMotion() })); }).catch(() => {});
    if (this.journey >= 0) controller.setScrollProgress(this.journey, JOURNEY_FRAMES);
    else globalThis.setTimeout(() => { if (this.controller === controller && this.journey < 0) controller.showRoutes(ROUTE_REVEAL, { stagger: 900 }); }, 700);
    if (!this.active) controller.pause();
  }

  destroy() { this.controller?.destroy(); this.controller = null; }

  setActive(active: boolean) {
    if (active === this.active) return; this.active = active;
    if (active) this.controller?.resume(); else this.controller?.pause();
  }

  /** Back to the free hero globe (rotation, all corridors). */
  hero() {
    if (this.journey < 0) return; this.journey = -1; const controller = this.controller; if (!controller) return;
    controller.releaseProgress(); controller.setAutoRotate(true); controller.showRoutes(ROUTE_REVEAL);
  }

  /** 0..1 along the China → Cartagena corridor. */
  setJourney(progress: number) {
    const first = this.journey < 0; this.journey = progress; const controller = this.controller; if (!controller) return;
    if (first) controller.setAutoRotate(false);
    controller.setScrollProgress(progress, JOURNEY_FRAMES);
  }

  diagnostics() { return this.controller?.diagnostics() ?? null; }
}
